import { create } from 'zustand';
import type { Timestamp } from 'firebase/firestore';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import { db, storage } from '../services/firebase';
import { ref, uploadString, getBytes, deleteObject } from 'firebase/storage';

export interface GenerationSettings {
  devices: ('mobile' | 'tablet' | 'desktop')[];
  detailLevel: number; // 1-5
  colors: string[]; // hex codes
  backgroundColor?: string; // hex
}

export interface Project {
  id: string;
  userId: string;
  name: string;
  templateType?: 'general' | 'landing' | 'mobile';
  canvasWidth?: number;
  canvasHeight?: number;
  html?: string;
  css?: string;
  components?: unknown;
  styles?: unknown;
  projectData?: unknown;
  contentPath?: string;
  contentVersion?: number;
  contentVersions?: number[];
  contentPathsByVersion?: Record<string, string>;
  contentVersionMeta?: Array<{
    id: string; // stable key, used for selecting/deleting
    path: string;
    comment?: string;
    createdAt?: number;
  }>;
  activeContentVersionId?: string;
  contentSaved?: boolean;
  thumbnail?: string;
  generationSettings?: GenerationSettings;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

interface ProjectContent {
  html?: string;
  css?: string;
  components?: unknown;
  styles?: unknown;
  projectData?: unknown;
  pageSnapshots?: Array<{ id: string; name: string; html: string; css: string }>;
}

type ProjectUpdate = Record<string, any>;

interface ProjectsState {
  projects: Project[];
  currentProject: Project | null;
  loading: boolean;
  error: string | null;
  useLocalStorage: boolean;

  loadProjects: (userId: string) => () => void;
  createProject: (
    userId: string,
    name: string,
    options?: { templateType?: Project['templateType']; canvasWidth?: number; canvasHeight?: number }
  ) => Promise<string>;
  updateProject: (projectId: string, data: ProjectUpdate, opts?: { touchUpdatedAt?: boolean }) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
  loadProjectContent: (projectId: string, opts?: { versionId?: string }) => Promise<ProjectContent | null>;
  setCurrentProjectVersion: (versionId: string) => Promise<void>;
  setCurrentProjectVersionComment: (versionId: string, comment: string) => Promise<void>;
  deleteCurrentProjectVersion: (versionId: string) => Promise<{ nextVersionId: string } | null>;
  saveCurrentContent: (content: ProjectContent, opts?: { mode?: 'overwrite' | 'new'; comment?: string }) => Promise<void>;
}

const CONTENT_VERSION = 1;

type VersionMetaEntry = NonNullable<Project['contentVersionMeta']>[number];

function getContentPath(userId: string, projectId: string) {
  return `users/${userId}/projects/${projectId}.json`;
}

function getVersionedContentPath(userId: string, projectId: string, version: number) {
  // Must stay within `/users/{userId}/projects/{projectFile}` because of current storage.rules.
  // So versions are stored as separate files in the same folder.
  return `users/${userId}/projects/${projectId}_v${version}.json`;
}

function getVersionedContentPathById(userId: string, projectId: string, versionId: string) {
  // Keep flat path (storage.rules restriction) and avoid collisions with v1 file.
  return `users/${userId}/projects/${projectId}__${versionId}.json`;
}

function sanitizeVersionMeta(meta: VersionMetaEntry[]): VersionMetaEntry[] {
  return meta
    .filter((m) => m && typeof m.id === 'string' && m.id && typeof m.path === 'string' && m.path)
    .map((m) => {
      const out: VersionMetaEntry = { id: m.id, path: m.path };
      const comment = typeof m.comment === 'string' ? m.comment.trim() : '';
      if (comment) out.comment = comment;
      if (typeof m.createdAt === 'number' && Number.isFinite(m.createdAt)) out.createdAt = m.createdAt;
      return out;
    });
}

function ensureVersionIndex(project: Project): { versions: number[]; pathsByVersion: Record<string, string> } {
  const currentVersion = project.contentVersion ?? 1;
  const versions = Array.isArray(project.contentVersions) && project.contentVersions.length > 0
    ? project.contentVersions.slice()
    : [currentVersion];
  const pathsByVersion: Record<string, string> = { ...(project.contentPathsByVersion ?? {}) };

  // If we don't have a mapping yet, treat existing contentPath as the current version path.
  if (!pathsByVersion[String(currentVersion)] && project.contentPath) {
    pathsByVersion[String(currentVersion)] = project.contentPath;
  }

  // Normalize old (now unauthorized) subfolder paths to the flat format.
  for (const v of versions) {
    const key = String(v);
    const legacy = `users/${project.userId}/projects/${project.id}/v${v}.json`;
    if (pathsByVersion[key] === legacy) {
      pathsByVersion[key] = v === 1 ? getContentPath(project.userId, project.id) : getVersionedContentPath(project.userId, project.id, v);
    }
  }

  // Also normalize a legacy "active" path if present.
  if (project.contentPath) {
    const legacyActivePrefix = `users/${project.userId}/projects/${project.id}/v`;
    if (project.contentPath.startsWith(legacyActivePrefix)) {
      const match = project.contentPath.match(/\/v(\d+)\.json$/);
      const v = match ? Number(match[1]) : NaN;
      if (Number.isFinite(v)) {
        pathsByVersion[String(v)] = v === 1 ? getContentPath(project.userId, project.id) : getVersionedContentPath(project.userId, project.id, v);
      }
    }
  }

  // Keep versions unique and sorted ascending for UI.
  const unique = Array.from(new Set(versions)).sort((a, b) => a - b);
  return { versions: unique, pathsByVersion };
}

function ensureVersionMeta(project: Project): {
  meta: NonNullable<Project['contentVersionMeta']>;
  activeId: string;
} {
  const existingRaw = Array.isArray(project.contentVersionMeta) ? project.contentVersionMeta.filter(Boolean) as VersionMetaEntry[] : [];
  const existing = sanitizeVersionMeta(existingRaw);
  if (existing.length > 0) {
    const activeId = project.activeContentVersionId ?? existing[existing.length - 1]!.id;
    return { meta: existing, activeId };
  }

  // Legacy migration from numeric versions.
  const { versions, pathsByVersion } = ensureVersionIndex(project);
  const sorted = versions.length ? versions.slice().sort((a, b) => a - b) : [project.contentVersion ?? 1];
  const meta = sorted.map((v) => ({
    id: `legacy_${v}`, // stable enough; stays unique per number
    path:
      pathsByVersion[String(v)] ||
      (v === 1 ? getContentPath(project.userId, project.id) : getVersionedContentPath(project.userId, project.id, v)),
  }));
  const activeNumeric = project.contentVersion ?? sorted[sorted.length - 1] ?? 1;
  const activeId = project.activeContentVersionId ?? `legacy_${activeNumeric}`;
  return { meta: sanitizeVersionMeta(meta), activeId };
}

async function fetchProjectContent(path: string): Promise<ProjectContent | null> {
  try {
    const bytes = await getBytes(ref(storage, path));
    const text = new TextDecoder().decode(bytes);
    const data = JSON.parse(text);
    if (data && typeof data === 'object') {
      return {
        components: data.components,
        styles: data.styles,
        html: data.html,
        css: data.css,
        projectData: (data as any).projectData,
        pageSnapshots: (data as any).pageSnapshots,
      };
    }
    return null;
  } catch (error: any) {
    if (error?.code === 'storage/object-not-found') {
      return null;
    }
    console.error('Error fetching project content from storage:', error);
    return null;
  }
}

export const useProjectsStore = create<ProjectsState>()((set, get) => ({
  projects: [],
  currentProject: null,
  loading: false,
  error: null,
  useLocalStorage: false,

  loadProjects: (userId: string) => {
    // Try to load from Firebase first
    const q = query(
      collection(db, 'projects'),
      where('userId', '==', userId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
          const projects = snapshot.docs
          .map((doc) => {
            const data = doc.data() as Project;
            const base = {
              id: doc.id,
              userId: data.userId,
              name: data.name,
              templateType: data.templateType,
              canvasWidth: (data as any).canvasWidth ?? undefined,
              canvasHeight: (data as any).canvasHeight ?? undefined,
              thumbnail: data.thumbnail,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              contentPath: data.contentPath,
              contentVersion: data.contentVersion,
              contentVersions: data.contentVersions,
              contentPathsByVersion: data.contentPathsByVersion,
              contentVersionMeta: data.contentVersionMeta,
              activeContentVersionId: data.activeContentVersionId,
              contentSaved: data.contentSaved,
              generationSettings: data.generationSettings,
            } as Project;

            // Ensure derived meta exists for legacy docs.
            if (!base.contentVersionMeta || base.contentVersionMeta.length === 0) {
              const derived = ensureVersionMeta(base);
              base.contentVersionMeta = derived.meta;
              base.activeContentVersionId = base.activeContentVersionId ?? derived.activeId;
            }

            return base;
          })
          .sort((a: any, b: any) => {
            const aSec = a.updatedAt?.seconds ?? 0;
            const bSec = b.updatedAt?.seconds ?? 0;
            return bSec - aSec;
          });
        set({ projects, loading: false, error: null, useLocalStorage: false });

        // Sync currentProject with updated data
        const current = get().currentProject;
        if (current) {
          const updated = projects.find((p: Project) => p.id === current.id);
          if (updated) {
            // Avoid dropping locally-known version index fields if the backend doc hasn't been updated yet.
            set({
              currentProject: {
                ...current,
                ...updated,
                contentVersions: updated.contentVersions ?? current.contentVersions,
                contentPathsByVersion: updated.contentPathsByVersion ?? current.contentPathsByVersion,
                contentVersionMeta: updated.contentVersionMeta ?? current.contentVersionMeta,
                activeContentVersionId: updated.activeContentVersionId ?? current.activeContentVersionId,
              },
            });
          }
        }
      },
      (error) => {
        console.error("Firestore error:", error);
        // If permission denied, fallback to local storage
        if (error.code === 'permission-denied') {
          console.warn('Falling back to LocalStorage due to Firestore permission setup.');
          const localData = localStorage.getItem(`projects_${userId}`);
          const projects = localData ? JSON.parse(localData) : [];
          set({ projects, loading: false, error: null, useLocalStorage: true });

          // Sync currentProject for local storage too
          const current = get().currentProject;
          if (current) {
            const updated = projects.find((p: Project) => p.id === current.id);
            if (updated) set({ currentProject: updated });
          }
        } else {
          set({ error: error.message, loading: false });
        }
      }
    );

    return unsubscribe;
  },

  createProject: async (userId: string, name: string, options) => {
    const { useLocalStorage, projects } = get();

    if (useLocalStorage) {
      const newProjectId = crypto.randomUUID();
      const initialPath = getContentPath(userId, newProjectId);
      const v1Id = crypto.randomUUID();
      const newProject: Project = {
        id: newProjectId,
        userId,
        name,
        templateType: options?.templateType ?? 'general',
        canvasWidth: options?.templateType === 'general' ? undefined : options?.canvasWidth,
        canvasHeight: options?.templateType === 'general' ? undefined : options?.canvasHeight,
        createdAt: { seconds: Date.now() / 1000, nanoseconds: 0 } as Timestamp,
        updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 } as Timestamp,
        contentPath: initialPath,
        contentVersion: 1,
        contentVersions: [1],
        contentPathsByVersion: { '1': initialPath },
        contentVersionMeta: [{ id: v1Id, path: initialPath, createdAt: Date.now() }],
        activeContentVersionId: v1Id,
        contentSaved: false,
      };
      const updatedProjects = [newProject, ...projects];
      set({ projects: updatedProjects, currentProject: newProject });
      localStorage.setItem(`projects_${userId}`, JSON.stringify(updatedProjects));
      return newProject.id;
    }

    try {
      const v1Id = crypto.randomUUID();
      const docRef = await addDoc(collection(db, 'projects'), {
        userId,
        name,
        templateType: options?.templateType ?? 'general',
        canvasWidth: options?.templateType === 'general' ? null : (options?.canvasWidth ?? null),
        canvasHeight: options?.templateType === 'general' ? null : (options?.canvasHeight ?? null),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        contentVersion: 1,
        contentVersions: [1],
        contentVersionMeta: [{ id: v1Id, path: '', createdAt: Date.now() }],
        activeContentVersionId: v1Id,
        contentSaved: false,
      });
      const contentPath = getContentPath(userId, docRef.id);
      await updateDoc(docRef, {
        contentPath,
        contentPathsByVersion: { '1': contentPath },
        contentVersionMeta: [{ id: v1Id, path: contentPath, createdAt: Date.now() }],
      });

      // Optimistic local insert so the editor route can open immediately.
      const optimistic: Project = {
        id: docRef.id,
        userId,
        name,
        templateType: options?.templateType ?? 'general',
        canvasWidth: options?.templateType === 'general' ? undefined : options?.canvasWidth,
        canvasHeight: options?.templateType === 'general' ? undefined : options?.canvasHeight,
        createdAt: { seconds: Date.now() / 1000, nanoseconds: 0 } as Timestamp,
        updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 } as Timestamp,
        contentPath,
        contentVersion: 1,
        contentVersions: [1],
        contentPathsByVersion: { '1': contentPath },
        contentVersionMeta: [{ id: v1Id, path: contentPath, createdAt: Date.now() }],
        activeContentVersionId: v1Id,
        contentSaved: false,
      };
      set((state) => {
        const exists = state.projects.some((p) => p.id === optimistic.id);
        return {
          projects: exists ? state.projects : [optimistic, ...state.projects],
          currentProject: optimistic,
        };
      });

      return docRef.id;
    } catch (error: any) {
      if (error.code === 'permission-denied') {
        // This might happen if loadProjects hasn't failed yet but create does
        // Simplify: just throw for now, let the user retry or let loadProjects handle the switch
        // Ideally we would switch here too, but loadProjects owns the source of truth list.
        throw error;
      }
      const message = error instanceof Error ? error.message : 'Failed to create project';
      set({ error: message });
      throw error;
    }
  },

  updateProject: async (projectId: string, data: ProjectUpdate, opts?: { touchUpdatedAt?: boolean }) => {
    const { useLocalStorage, projects } = get();
    const touchUpdatedAt = opts?.touchUpdatedAt !== false;

    if (useLocalStorage) {
      const updatedProjects = projects.map((p: Project) =>
        p.id === projectId
          ? {
              ...p,
              ...data,
              ...(touchUpdatedAt ? { updatedAt: { seconds: Date.now() / 1000, nanoseconds: 0 } as Timestamp } : null),
            }
          : p
      );
      set({ projects: updatedProjects });

      // Update local currentProject state if it's the one being updated
      const { currentProject } = get();
      if (currentProject && currentProject.id === projectId) {
        set({ currentProject: { ...currentProject, ...data } });
      }

      // We need the userId to save to the right key. 
      // Assuming simplistic single-user valid session for local storage fallback
      // We'll find the project to get the userId or use the one from the project being updated
      const project = projects.find((p: Project) => p.id === projectId);
      if (project) {
        localStorage.setItem(`projects_${project.userId}`, JSON.stringify(updatedProjects));
      }
      return;
    }

    try {
      const docRef = doc(db, 'projects', projectId);
      const payload: Record<string, any> = { ...data };
      if (touchUpdatedAt) payload.updatedAt = serverTimestamp();
      await updateDoc(docRef, payload);

      // Update local currentProject state if it's the one being updated
      const { currentProject } = get();
      if (currentProject && currentProject.id === projectId) {
        set({ currentProject: { ...currentProject, ...data } });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update project';
      set({ error: message });
      throw error;
    }
  },

  deleteProject: async (projectId: string) => {
    const { useLocalStorage, projects } = get();

    if (useLocalStorage) {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      const updatedProjects = projects.filter(p => p.id !== projectId);
      set({ projects: updatedProjects });
      localStorage.setItem(`projects_${project.userId}`, JSON.stringify(updatedProjects));
      return;
    }

    try {
      await deleteDoc(doc(db, 'projects', projectId));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete project';
      set({ error: message });
      throw error;
    }
  },

  setCurrentProject: (project) => set({ currentProject: project }),

  loadProjectContent: async (projectId: string, opts?: { versionId?: string }) => {
    const { projects, useLocalStorage } = get();
    const project = projects.find((p) => p.id === projectId);
    if (!project) return null;

    if (useLocalStorage) {
      const localData = localStorage.getItem(`projects_${project.userId}`);
      if (!localData) return null;
      const list = JSON.parse(localData) as Project[];
      const match = list.find((p) => p.id === projectId);
      if (!match) return null;
      return { html: match.html ?? '', css: match.css ?? '', projectData: (match as any).projectData };
    }

    const { meta, activeId } = ensureVersionMeta(project);
    const requestedId = opts?.versionId;
    const effectiveId = requestedId ?? project.activeContentVersionId ?? activeId;
    const entry = meta.find((m) => m.id === effectiveId) ?? meta[meta.length - 1];
    const candidatePaths = [entry?.path, project.contentPath, getContentPath(project.userId, project.id)].filter(Boolean) as string[];
    const uniquePaths = Array.from(new Set(candidatePaths));

    const forceStorage = requestedId != null || meta.length > 0;
    if (forceStorage || project.contentSaved !== false) {
      for (const path of uniquePaths) {
        const content = await fetchProjectContent(path);
        if (content) {
          if (!project.contentSaved) {
            // Avoid bumping updatedAt just because we discovered legacy content is stored.
            await get().updateProject(project.id, { contentSaved: true }, { touchUpdatedAt: false });
          }
          return content;
        }
      }
    }

    try {
      // Fallback for legacy docs that still have html/css
      const docSnap = await getDoc(doc(db, 'projects', projectId));
      if (docSnap.exists()) {
        const data = docSnap.data() as Project;
        return { html: data.html ?? '', css: data.css ?? '', components: data.components, styles: data.styles };
      }
      return { html: '', css: '' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load project content';
      set({ error: message });
      return null;
    }
  },

  setCurrentProjectVersion: async (versionId: string) => {
    const { currentProject, useLocalStorage, projects } = get();
    if (!currentProject) return;

    const indexProject = projects.find((p) => p.id === currentProject.id) ?? currentProject;
    const { meta: metaRaw } = ensureVersionMeta(indexProject);
    const meta = sanitizeVersionMeta(metaRaw);
    const entry = meta.find((m) => m.id === versionId);
    if (!entry) return;

    const updated: Project = {
      ...currentProject,
      activeContentVersionId: versionId,
      contentPath: entry.path,
      contentVersionMeta: meta,
    };

    set({ currentProject: updated });

    // Persist "active" version so reloads open the last selected version.
    if (useLocalStorage) {
      const localData = localStorage.getItem(`projects_${currentProject.userId}`);
      const list = localData ? (JSON.parse(localData) as Project[]) : [];
      const next = list.map((p) =>
        p.id === currentProject.id
          ? {
              ...p,
              activeContentVersionId: versionId,
              contentPath: entry.path,
              contentVersionMeta: meta,
            }
          : p
      );
      localStorage.setItem(`projects_${currentProject.userId}`, JSON.stringify(next));
      set({ projects: next });
      return;
    }

    // Switching version should not reorder the project list (which is sorted by updatedAt).
    await get().updateProject(
      currentProject.id,
      {
      activeContentVersionId: versionId,
      contentPath: entry.path,
      contentVersionMeta: meta,
      },
      { touchUpdatedAt: false }
    );
  },

  setCurrentProjectVersionComment: async (versionId: string, comment: string) => {
    const { currentProject, useLocalStorage, projects } = get();
    if (!currentProject) return;

    const indexProject = projects.find((p) => p.id === currentProject.id) ?? currentProject;
    const { meta, activeId } = ensureVersionMeta(indexProject);
    const effectiveId = versionId || currentProject.activeContentVersionId || activeId;
    const nextMetaRaw = meta.map((m) => (m.id === effectiveId ? { ...m, comment } : m));
    const nextMeta = sanitizeVersionMeta(nextMetaRaw);

    const updated: Project = { ...currentProject, contentVersionMeta: nextMeta };
    set((state) => ({
      currentProject: updated,
      projects: state.projects.map((p) => (p.id === currentProject.id ? { ...p, contentVersionMeta: nextMeta } : p)),
    }));

    if (useLocalStorage) {
      const localData = localStorage.getItem(`projects_${currentProject.userId}`);
      const list = localData ? (JSON.parse(localData) as Project[]) : [];
      const next = list.map((p) => (p.id === currentProject.id ? { ...p, contentVersionMeta: nextMeta } : p));
      localStorage.setItem(`projects_${currentProject.userId}`, JSON.stringify(next));
      set({ projects: next });
      return;
    }

    // Comment edits shouldn't change project ordering.
    await get().updateProject(currentProject.id, { contentVersionMeta: nextMeta }, { touchUpdatedAt: false });
  },

  deleteCurrentProjectVersion: async (versionId: string) => {
    const { currentProject, useLocalStorage, projects } = get();
    if (!currentProject) return null;

    const indexProject = projects.find((p) => p.id === currentProject.id) ?? currentProject;
    const { meta, activeId } = ensureVersionMeta(indexProject);
    const effectiveId = versionId || currentProject.activeContentVersionId || activeId;
    const entry = meta.find((m) => m.id === effectiveId);
    if (!entry) return null;
    if (meta.length <= 1) {
      throw new Error('cannot-delete-last-version');
    }

    const nextMeta = sanitizeVersionMeta(meta.filter((m) => m.id !== effectiveId));
    const wasActive = (currentProject.activeContentVersionId ?? activeId) === effectiveId;
    const nextActiveId = wasActive ? nextMeta[nextMeta.length - 1]!.id : (currentProject.activeContentVersionId ?? activeId);
    const nextActiveEntry = nextMeta.find((m) => m.id === nextActiveId) ?? nextMeta[nextMeta.length - 1]!;

    // Optimistic local update: remove version immediately from picker.
    const optimistic: Project = {
      ...currentProject,
      contentVersionMeta: nextMeta,
      activeContentVersionId: nextActiveId,
      contentPath: nextActiveEntry.path,
    };
    set((state) => ({
      currentProject: optimistic,
      projects: state.projects.map((p) =>
        p.id === currentProject.id
          ? {
              ...p,
              contentVersionMeta: optimistic.contentVersionMeta,
              activeContentVersionId: optimistic.activeContentVersionId,
              contentPath: optimistic.contentPath,
            }
          : p
      ),
    }));

    // Delete version file in Storage (ignore not-found).
    if (!useLocalStorage && entry.path) {
      try {
        await deleteObject(ref(storage, entry.path));
      } catch (error: any) {
        if (error?.code !== 'storage/object-not-found') {
          console.error('Failed to delete version object:', error);
        }
      }
    }

    // Persist index update.
    if (useLocalStorage) {
      const localData = localStorage.getItem(`projects_${currentProject.userId}`);
      const list = localData ? (JSON.parse(localData) as Project[]) : [];
      const next = list.map((p) =>
        p.id === currentProject.id
          ? {
              ...p,
              contentVersionMeta: optimistic.contentVersionMeta,
              activeContentVersionId: optimistic.activeContentVersionId,
              contentPath: optimistic.contentPath,
            }
          : p
      );
      localStorage.setItem(`projects_${currentProject.userId}`, JSON.stringify(next));
      set({ projects: next, currentProject: optimistic });
      return { nextVersionId: nextActiveId };
    }

    await get().updateProject(currentProject.id, {
      contentVersionMeta: optimistic.contentVersionMeta,
      activeContentVersionId: optimistic.activeContentVersionId,
      contentPath: optimistic.contentPath,
    });

    return { nextVersionId: nextActiveId };
  },

  saveCurrentContent: async (content: ProjectContent, opts?: { mode?: 'overwrite' | 'new'; comment?: string }) => {
    const { currentProject } = get();
    if (!currentProject) return;

    try {
      const { useLocalStorage } = get();
      const mode = opts?.mode ?? 'overwrite';
      const comment = (opts?.comment ?? '').trim() || undefined;

      if (useLocalStorage) {
        // LocalStorage path doesn't currently support multi-file storage; keep it simple.
        // We still store meta for UI and comments.
        const { meta: metaRaw, activeId } = ensureVersionMeta(currentProject);
        const meta = sanitizeVersionMeta(metaRaw);
        const active = currentProject.activeContentVersionId ?? activeId;
        const now = Date.now();
        let nextMeta = meta.slice();
        let nextActiveId = active;

        if (mode === 'new') {
          const id = crypto.randomUUID();
          nextActiveId = id;
          nextMeta = sanitizeVersionMeta([...nextMeta, { id, path: `local:${id}`, comment, createdAt: now }]);
        } else if (comment !== undefined) {
          nextMeta = sanitizeVersionMeta(nextMeta.map((m) => (m.id === active ? { ...m, comment } : m)));
        }

        set({ currentProject: { ...currentProject, contentVersionMeta: nextMeta, activeContentVersionId: nextActiveId } });

        await get().updateProject(currentProject.id, {
          html: content.html ?? '',
          css: content.css ?? '',
          components: content.components,
          styles: content.styles,
          projectData: content.projectData,
          contentVersionMeta: nextMeta as any,
          activeContentVersionId: nextActiveId as any,
        } as any);
        return;
      }

      const { meta: metaRaw, activeId } = ensureVersionMeta(currentProject);
      const meta = sanitizeVersionMeta(metaRaw);
      const now = Date.now();
      let nextMeta = meta.slice();
      let nextActiveId = currentProject.activeContentVersionId ?? activeId;
      let path: string | undefined;

      if (mode === 'new') {
        const id = crypto.randomUUID();
        nextActiveId = id;
        path = getVersionedContentPathById(currentProject.userId, currentProject.id, id);
        nextMeta = sanitizeVersionMeta([...nextMeta, { id, path, comment, createdAt: now }]);
      } else {
        const entry = nextMeta.find((m) => m.id === nextActiveId) ?? nextMeta[nextMeta.length - 1]!;
        path = entry.path;
        if (comment !== undefined) {
          nextMeta = sanitizeVersionMeta(nextMeta.map((m) => (m.id === entry.id ? { ...m, comment } : m)));
        }
      }

      if (!path) path = getContentPath(currentProject.userId, currentProject.id);

      // Optimistically update local state so the version picker updates immediately,
      // even if Firestore update is delayed or fails.
      const optimistic: Project = {
        ...currentProject,
        contentPath: path,
        contentVersionMeta: nextMeta,
        activeContentVersionId: nextActiveId,
      };
      set((state) => ({
        currentProject: optimistic,
        projects: state.projects.map((p) =>
          p.id === currentProject.id
            ? {
                ...p,
                contentPath: optimistic.contentPath,
                contentVersionMeta: optimistic.contentVersionMeta,
                activeContentVersionId: optimistic.activeContentVersionId,
              }
            : p
        ),
      }));

      const payload = JSON.stringify({
        version: CONTENT_VERSION,
        components: content.components,
        styles: content.styles,
        html: content.html,
        css: content.css,
        projectData: content.projectData,
        pageSnapshots: content.pageSnapshots,
      });

      await uploadString(ref(storage, path), payload, 'raw', {
        contentType: 'application/json',
      });

      await get().updateProject(currentProject.id, {
        contentPath: path,
        contentVersionMeta: nextMeta,
        activeContentVersionId: nextActiveId,
        contentSaved: true,
        html: deleteField(),
        css: deleteField(),
      });

      set((state) => ({
        currentProject: state.currentProject
          ? { ...state.currentProject, contentSaved: true }
          : state.currentProject,
      }));
    } catch (error) {
      console.error('Failed to save content:', error);
      const message = error instanceof Error ? error.message : 'Failed to save content';
      set({ error: message });
    }
  },
}));
